// Portal data layer: fetch from the backend and adapt to the design's
// view-models. As of R2 the project/client fields are real columns.
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { STAGE_LABELS, type Stage, type ProjectStatus } from "../types";

export interface ApiProject {
  id: string;
  client_id: string;
  client_name: string;
  title: string;
  brief: string;
  stage: Stage;
  status: ProjectStatus;
  code: string;
  summary: string;
  health: "on-track" | "at-risk";
  progress: number;
  started_at: number | null;
  due_at: number | null;
  hours_used: number | null;
  hours_budget: number | null;
  lead: string;
  stack: string; // JSON array
  requires_signoff?: number; // O3: releases need client sign-off on sandbox
  cadence?: string;
  risk?: string; // O4: amber callout on the project card
  created_at: number;
  updated_at: number;
}

export interface ApiClient {
  id: string;
  name: string;
  contact: string;
  plan: string;
  since: string;
  created_at: number;
  archived_at: number | null;
}

export interface ApiTicket {
  id: string;
  project_id: string;
  type: "issue" | "change";
  title: string;
  body: string;
  status: "open" | "in_progress" | "resolved" | "closed";
  priority: "low" | "normal" | "high" | "urgent";
  category: string;
  board_status: string;
  assignee: string;
  environment: string;
  channel: string;
  tags: string; // JSON
  logged_hours: number | null;
  // Target clock (O2). Kind/due/paused are stored; the rest is derived by the
  // API on read (functions/_lib/sla.ts).
  sla_kind: "first_reply" | "fix" | "";
  sla_due_at: number | null;
  sla_paused_at: number | null;
  sla_tone: "danger" | "warning" | "neutral";
  sla_label: string;
  sla_short: string;
  sla_pct: number;
  sla_note: string;
  linked: string;
  release_id?: string | null; // O3
  ready?: "no" | "sandbox" | "live";
  due_date: number | null;
  author_email: string | null;
  author_name: string | null;
  author_side: "team" | "client";
  // Derived server-side: last client-visible message is from the client.
  needs_reply: number;
  created_at: number;
  updated_at: number;
}

export interface ApiCommentFile {
  id: string;
  name: string;
  content_type: string;
  size: number;
}

export interface ApiComment {
  id: string;
  body: string;
  internal: number;
  files?: ApiCommentFile[];
  side: "team" | "client";
  author_email: string | null;
  author_name: string | null;
  created_at: number;
}

export interface ApiHistory {
  id: string;
  at: number;
  text: string;
  state: string;
}

export interface ApiMilestone {
  id: string;
  project_id: string;
  name: string;
  state: "done" | "active" | "next";
  date_at: number | null;
  note: string;
  sort_order: number;
  // O4: client sign-off.
  signoff?: "pending" | "done" | null;
  signed_off_by?: string;
  signed_off_at?: number | null;
  signoff_requested_at?: number | null;
}

export interface ApiChangeRequest {
  id: string;
  project_id: string;
  ref: string;
  title: string;
  detail: string;
  estimate_hours: number | null;
  cost_pence: number | null;
  state: string;
  raised_at: number;
  // O4: ticket link + decision.
  ticket_id?: string | null;
  decided_by?: string;
  decided_at?: number | null;
  scheduled_release_id?: string | null;
  delivered_at?: number | null;
  reminded_at?: number | null;
}

export interface VMilestone {
  id: string;
  projectId: string;
  name: string;
  state: "done" | "active" | "next";
  date: string;
  dateAt: number | null;
  note: string;
  sortOrder: number;
  signoff: "pending" | "done" | null;
  signedOffBy: string;
  signedOffAt: number | null;
  signoffRequestedAt: number | null;
}

export interface VChangeRequest {
  id: string;
  projectId: string;
  ref: string;
  title: string;
  detail: string;
  estimate: string;
  estimateHours: number | null;
  costPence: number | null; // stored, else hours × rate
  cost: string;
  state: string;
  raised: string;
  raisedAt: number;
  ticketId: string | null;
  decidedBy: string;
  decidedAt: number | null;
  scheduledReleaseId: string | null;
  deliveredAt: number | null;
  remindedAt: number | null;
}

export interface ApiCredential {
  id: string;
  client_id: string;
  project_id: string | null;
  label: string;
  username: string;
  env: string;
  cred_type: string;
  url: string;
  client_visible: number;
  updated_at: number;
  created_by_email: string | null;
}

export interface ApiDocument {
  id: string;
  client_id: string;
  project_id: string | null;
  name: string;
  kind: string;
  content_type: string;
  size: number;
  created_at: number;
  added_by_email: string | null;
  added_by_name?: string | null;
}

export interface VCredential {
  id: string;
  clientId: string;
  projectId: string | null; // null = client account
  label: string;
  username: string;
  env: string;
  type: string;
  url: string;
  clientVisible: boolean;
  updated: string;
}

export interface VDocument {
  id: string;
  clientId: string;
  projectId: string | null; // null = client account
  name: string;
  kind: string;
  size: string;
  addedBy: string;
  added: string;
}

export interface ApiTask {
  id: string;
  client_id: string;
  project_id: string | null;
  kind: "document" | "action";
  title: string;
  detail: string;
  state: string;
  due_at: number | null;
  document_id: string | null;
  document_name: string | null;
  document_size: number | null;
  documents?: { id: string; name: string; size: number }[];
  allow_multiple?: number;
  ticket_id?: string | null; // O4
  reminded_at?: number | null; // O4
  completed_at: number | null;
  created_by_email: string | null;
  created_by_name?: string | null;
  comment_count: number | null;
  created_at: number;
  updated_at: number;
}

export interface VTask {
  id: string;
  clientId: string;
  projectId: string | null; // null = client account
  kind: "document" | "action";
  title: string;
  detail: string;
  state: string; // Requested | Submitted | Done | Cancelled
  due: string;
  dueAt: number | null;
  overdue: boolean;
  documents: { id: string; name: string; size: string }[];
  allowMultiple: boolean; // document requests: may the client upload many files
  commentCount: number;
  created: string;
  createdBy: string; // requester's name, else email
  ticketId: string | null;
  remindedAt: number | null;
  completedAt: number | null;
  updatedAt: number;
}

/** Activity filter category (derived from the event kind server-side). */
export type ActivityKind = "ticket" | "release" | "task" | "change" | "file" | "access";

export interface VActivity {
  id: string;
  projectId: string | null; // null = client account
  clientId: string;
  icon: string;
  kind: ActivityKind;
  text: string;
  who: string;
  at: number;
  internal: boolean; // admin only; never sent to clients
}

export interface VClient {
  id: string;
  name: string;
  contact: string;
  plan: string;
  since: string;
  archived: boolean;
}

export interface VProject {
  id: string;
  clientId: string;
  clientName: string;
  name: string;
  code: string;
  status: string;
  health: "on-track" | "at-risk";
  progress: number;
  summary: string;
  due: string;
  hours: string;
  stack: string[];
  lead: string;
  requiresSignoff: boolean; // releases need client sign-off on sandbox
  cadence: string; // e.g. "Fortnightly, Tuesdays"
  risk: string; // amber callout on the card ("" = none)
  // Raw values for the admin edit form.
  raw: {
    title: string;
    brief: string;
    stage: Stage;
    status: ProjectStatus;
    startedAt: number | null;
    dueAt: number | null;
    hoursUsed: number | null;
    hoursBudget: number | null;
  };
}

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export interface VTicket {
  id: string;
  projectId: string;
  type: "issue" | "change"; // legacy, for change-request counting
  category: string; // Issue | Question | Change
  subject: string;
  body: string;
  status: string; // board status
  priority: string; // capitalised
  assignee: string;
  environment: string;
  channel: string;
  tags: string[];
  logged: string;
  clock: TicketClock;
  linked: string;
  releaseId: string | null;
  ready: "no" | "sandbox" | "live";
  author: string; // display name, else email
  authorEmail: string;
  authorSide: "team" | "client";
  needsReply: boolean;
  dueAt: number | null;
  opened: number;
  updatedAt: number;
}

/** The ticket's target clock as the API derives it. kind "" = no clock. */
export interface TicketClock {
  kind: "first_reply" | "fix" | "";
  kindLabel: string; // "First reply" | "Fix target" | ""
  dueAt: number | null;
  paused: boolean;
  tone: "danger" | "warning" | "neutral";
  label: string; // "3h 10m left" · "25m overdue" · "Due Fri 9 Oct" · "Paused"
  short: string; // list chip text ("" = no chip)
  pct: number; // share of the target used, 0–100
  note: string; // "Target: 1 working day for High."
}

/** Fallback pseudo-code from the title when no code is set. */
function deriveCode(title: string): string {
  const initials = title
    .split(/\s+/)
    .map((w) => w.replace(/[^A-Za-z0-9]/g, "")[0])
    .filter(Boolean)
    .join("")
    .toUpperCase()
    .slice(0, 6);
  return initials || "PROJ";
}

export function fmtDay(ms: number | null): string {
  if (!ms) return "";
  return new Date(ms).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
  });
}

export function fmtDate(ms: number | null): string {
  if (!ms) return "";
  return new Date(ms).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function fmtCost(pence: number | null): string {
  if (pence == null) return "";
  const pounds = pence / 100;
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: pounds % 1 === 0 ? 0 : 2,
  }).format(pounds);
}

export function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function adaptCredential(c: ApiCredential): VCredential {
  return {
    id: c.id,
    clientId: c.client_id,
    projectId: c.project_id,
    label: c.label,
    username: c.username,
    env: c.env,
    type: c.cred_type,
    url: c.url,
    clientVisible: c.client_visible !== 0,
    updated: fmtDay(c.updated_at),
  };
}

function adaptDocument(d: ApiDocument): VDocument {
  return {
    id: d.id,
    clientId: d.client_id,
    projectId: d.project_id,
    name: d.name,
    kind: d.kind,
    size: fmtSize(d.size),
    addedBy: d.added_by_name || d.added_by_email || "Agile Software",
    added: fmtDay(d.created_at),
  };
}

function adaptTask(t: ApiTask): VTask {
  const open = t.state === "Requested";
  return {
    id: t.id,
    clientId: t.client_id,
    projectId: t.project_id,
    kind: t.kind,
    title: t.title,
    detail: t.detail,
    state: t.state,
    due: fmtDate(t.due_at),
    dueAt: t.due_at,
    overdue: open && t.due_at != null && t.due_at < Date.now(),
    documents: (
      t.documents ??
      // Fallback for older API shape: the single primary document, if any.
      (t.document_id
        ? [
            {
              id: t.document_id,
              name: t.document_name ?? "Document",
              size: t.document_size ?? 0,
            },
          ]
        : [])
    ).map((d) => ({ id: d.id, name: d.name, size: fmtSize(d.size) })),
    allowMultiple: t.allow_multiple !== 0,
    commentCount: t.comment_count ?? 0,
    created: fmtDay(t.created_at),
    createdBy: t.created_by_name || t.created_by_email || "Agile Software",
    ticketId: t.ticket_id ?? null,
    remindedAt: t.reminded_at ?? null,
    completedAt: t.completed_at,
    updatedAt: t.updated_at,
  };
}

function adaptMilestone(m: ApiMilestone): VMilestone {
  return {
    id: m.id,
    projectId: m.project_id,
    name: m.name,
    state: m.state,
    date: fmtDate(m.date_at),
    dateAt: m.date_at,
    note: m.note,
    sortOrder: m.sort_order,
    signoff: m.signoff ?? null,
    signedOffBy: m.signed_off_by ?? "",
    signedOffAt: m.signed_off_at ?? null,
    signoffRequestedAt: m.signoff_requested_at ?? null,
  };
}

function adaptChangeRequest(cr: ApiChangeRequest, ratePence: number): VChangeRequest {
  const costPence =
    cr.cost_pence ?? (cr.estimate_hours == null ? null : Math.round(cr.estimate_hours * ratePence));
  return {
    id: cr.id,
    projectId: cr.project_id,
    ref: cr.ref,
    title: cr.title,
    detail: cr.detail,
    estimate:
      cr.estimate_hours == null
        ? ""
        : `${cr.estimate_hours} ${cr.estimate_hours === 1 ? "hour" : "hours"}`,
    estimateHours: cr.estimate_hours,
    costPence,
    cost: fmtCost(costPence),
    state: cr.state,
    raised: fmtDay(cr.raised_at),
    raisedAt: cr.raised_at,
    ticketId: cr.ticket_id ?? null,
    decidedBy: cr.decided_by ?? "",
    decidedAt: cr.decided_at ?? null,
    scheduledReleaseId: cr.scheduled_release_id ?? null,
    deliveredAt: cr.delivered_at ?? null,
    remindedAt: cr.reminded_at ?? null,
  };
}

function fmtHours(used: number | null, budget: number | null): string {
  if (used == null && budget == null) return "";
  if (used != null && budget != null) return `${used} / ${budget}h`;
  if (used != null) return `${used}h`;
  return `— / ${budget}h`;
}

function parseStack(json: string): string[] {
  try {
    const v = JSON.parse(json);
    return Array.isArray(v) ? v.map(String) : [];
  } catch {
    return [];
  }
}

function adaptProject(p: ApiProject): VProject {
  const complete = p.status === "complete";
  return {
    id: p.id,
    clientId: p.client_id,
    clientName: p.client_name,
    name: p.title,
    code: p.code || deriveCode(p.title),
    status: complete ? "Live" : STAGE_LABELS[p.stage],
    health: p.health === "at-risk" ? "at-risk" : "on-track",
    progress: p.progress,
    summary: p.summary,
    due: fmtDay(p.due_at),
    hours: fmtHours(p.hours_used, p.hours_budget),
    stack: parseStack(p.stack),
    lead: p.lead,
    requiresSignoff: p.requires_signoff !== 0,
    cadence: p.cadence ?? "",
    risk: p.risk ?? "",
    raw: {
      title: p.title,
      brief: p.brief,
      stage: p.stage,
      status: p.status,
      startedAt: p.started_at,
      dueAt: p.due_at,
      hoursUsed: p.hours_used,
      hoursBudget: p.hours_budget,
    },
  };
}

function adaptTicket(t: ApiTicket): VTicket {
  return {
    id: t.id,
    projectId: t.project_id,
    type: t.type,
    category: t.category || (t.type === "change" ? "Change" : "Issue"),
    subject: t.title,
    body: t.body ?? "",
    status: t.board_status || "Open",
    priority: cap(t.priority),
    assignee: t.assignee || "Unassigned",
    environment: t.environment || "",
    channel: t.channel || "Portal",
    tags: parseStack(t.tags),
    logged: t.logged_hours == null ? "" : `${t.logged_hours}h`,
    clock: {
      kind: t.sla_kind || "",
      kindLabel:
        t.sla_kind === "first_reply" ? "First reply" : t.sla_kind === "fix" ? "Fix target" : "",
      dueAt: t.sla_due_at ?? null,
      paused: t.sla_paused_at != null,
      tone: t.sla_tone ?? "neutral",
      label: t.sla_label ?? "",
      short: t.sla_short ?? "",
      pct: t.sla_pct ?? 0,
      note: t.sla_note ?? "",
    },
    linked: t.linked || "",
    releaseId: t.release_id ?? null,
    ready: t.ready === "sandbox" || t.ready === "live" ? t.ready : "no",
    author: t.author_name || t.author_email || "—",
    authorEmail: t.author_email || "",
    authorSide: t.author_side === "client" ? "client" : "team",
    needsReply: !!t.needs_reply,
    dueAt: t.due_date ?? null,
    opened: t.created_at,
    updatedAt: t.updated_at,
  };
}

export const isOpen = (t: VTicket) => t.status !== "Closed";

/* ---------- Releases (portal overhaul O3) ---------- */

export type ReleaseStatus =
  | "Planning"
  | "Open"
  | "On sandbox"
  | "Released"
  | "Immediate fixes";
export type ChangeKind = "add" | "improve" | "fix" | "remove";

export interface ApiRelease {
  id: string;
  client_id: string;
  project_id: string;
  version: string;
  name: string;
  status: ReleaseStatus;
  go_live_at: number | null;
  cutoff_at: number | null;
  approve_by: number | null;
  signoff: "pending" | "done" | "later" | "na";
  signed_off_by: string;
  signed_off_at: number | null;
  signoff_requested_at: number | null;
  sandbox_url: string;
  notes_state: "none" | "draft" | "scheduled" | "sent" | "na";
  notes_send: "when-live" | "now";
  notes_recipients: string; // JSON array of emails ("[]" for clients)
  notes_sent_at: number | null;
  released_at: number | null;
  created_at: number;
  project_title: string;
  project_code: string;
  requires_signoff: number;
  cadence: string;
}

export interface ApiReleaseChange {
  id: string;
  release_id: string;
  ticket_id: string | null;
  kind: ChangeKind;
  summary: string;
  details?: string; // admin only
  sort_order: number;
  ticket_ready: "no" | "sandbox" | "live" | null;
}

export interface VRelease {
  id: string;
  clientId: string;
  projectId: string;
  projectName: string;
  projectCode: string;
  requiresSignoff: boolean;
  version: string;
  isFixes: boolean; // the project's Immediate fixes lane
  name: string; // "Trade portal 2.4" / "Trade portal · immediate fixes"
  note: string; // optional free-text sub-line
  status: ReleaseStatus;
  goLiveAt: number | null;
  cutoffAt: number | null;
  approveBy: number | null;
  signoff: ApiRelease["signoff"];
  signedOffBy: string;
  signedOffAt: number | null;
  signoffRequestedAt: number | null;
  sandboxUrl: string;
  notesState: ApiRelease["notes_state"];
  notesSend: ApiRelease["notes_send"];
  notesRecipients: string[];
  releasedAt: number | null;
  createdAt: number;
}

export interface VReleaseChange {
  id: string;
  releaseId: string;
  ticketId: string | null;
  kind: ChangeKind;
  summary: string;
  details: string; // "" for clients
  ticketReady: "no" | "sandbox" | "live" | null;
}

export const FIXES_VERSION = "fixes";

function adaptRelease(r: ApiRelease): VRelease {
  const isFixes = r.version === FIXES_VERSION;
  return {
    id: r.id,
    clientId: r.client_id,
    projectId: r.project_id,
    projectName: r.project_title,
    projectCode: r.project_code || deriveCode(r.project_title),
    requiresSignoff: r.requires_signoff !== 0,
    version: r.version,
    isFixes,
    name: isFixes ? `${r.project_title} · immediate fixes` : `${r.project_title} ${r.version}`,
    note: r.name ?? "",
    status: r.status,
    goLiveAt: r.go_live_at,
    cutoffAt: r.cutoff_at,
    approveBy: r.approve_by,
    signoff: r.signoff,
    signedOffBy: r.signed_off_by ?? "",
    signedOffAt: r.signed_off_at,
    signoffRequestedAt: r.signoff_requested_at,
    sandboxUrl: r.sandbox_url ?? "",
    notesState: r.notes_state,
    notesSend: r.notes_send,
    notesRecipients: parseStack(r.notes_recipients),
    releasedAt: r.released_at,
    createdAt: r.created_at,
  };
}

function adaptChange(c: ApiReleaseChange): VReleaseChange {
  return {
    id: c.id,
    releaseId: c.release_id,
    ticketId: c.ticket_id,
    kind: c.kind,
    summary: c.summary,
    details: c.details ?? "",
    ticketReady: c.ticket_ready,
  };
}

export interface TicketDetail {
  ticket: VTicket;
  comments: ApiComment[];
  history: ApiHistory[];
}

/** Fetch one ticket with its conversation + history. */
export async function fetchTicketDetail(
  projectId: string,
  ticketId: string,
): Promise<TicketDetail> {
  const r = await api.get<{
    ticket: ApiTicket;
    comments: ApiComment[];
    history: ApiHistory[];
  }>(`/projects/${projectId}/tickets/${ticketId}`);
  return {
    ticket: adaptTicket(r.ticket),
    comments: r.comments,
    history: r.history ?? [],
  };
}

/** One ticket's detail (conversation + history), refetchable. */
export function useTicketDetail(
  projectId: string | null,
  ticketId: string | null,
) {
  const [detail, setDetail] = useState<TicketDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const refetch = useCallback(() => {
    if (!projectId || !ticketId) {
      setDetail(null);
      return;
    }
    setLoading(true);
    fetchTicketDetail(projectId, ticketId)
      .then(setDetail)
      .catch(() => setDetail(null))
      .finally(() => setLoading(false));
  }, [projectId, ticketId]);
  useEffect(refetch, [refetch]);
  return { detail, loading, refetch };
}

export interface TaskComment {
  id: string;
  body: string;
  side: "team" | "client";
  author_email: string | null;
  author_name?: string | null;
  created_at: number;
}

/** Fetch the comment thread for one task. */
export async function fetchTaskComments(taskId: string): Promise<TaskComment[]> {
  const r = await api.get<{ comments: TaskComment[] }>(
    `/tasks/${taskId}/comments`,
  );
  return r.comments ?? [];
}

/** Post a comment on a task. */
export async function postTaskComment(
  taskId: string,
  body: string,
): Promise<void> {
  await api.post(`/tasks/${taskId}/comments`, { body });
}

export interface PortalData {
  loading: boolean;
  error: string | null;
  clients: VClient[];
  projects: VProject[];
  tickets: VTicket[];
  milestones: VMilestone[];
  changeRequests: VChangeRequest[];
  credentials: VCredential[];
  documents: VDocument[];
  tasks: VTask[];
  activity: VActivity[];
  releases: VRelease[];
  releaseChanges: VReleaseChange[];
  crRatePence: number; // change-request rate (pence/hour, ex VAT) from the API
  reload: () => void;
}

/** Fallback until the API answers: £60/h. */
const DEFAULT_CR_RATE_PENCE = 6000;

export function usePortalData(isAdmin: boolean): PortalData {
  const [loading, setLoading] = useState(true);
  // Only the first load shows the loading state; later reloads refresh in
  // place so pages (e.g. the ticket inbox) keep their selection and layout.
  const loaded = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [clients, setClients] = useState<VClient[]>([]);
  const [projects, setProjects] = useState<VProject[]>([]);
  const [tickets, setTickets] = useState<VTicket[]>([]);
  const [milestones, setMilestones] = useState<VMilestone[]>([]);
  const [changeRequests, setChangeRequests] = useState<VChangeRequest[]>([]);
  const [credentials, setCredentials] = useState<VCredential[]>([]);
  const [documents, setDocuments] = useState<VDocument[]>([]);
  const [tasks, setTasks] = useState<VTask[]>([]);
  const [activity, setActivity] = useState<VActivity[]>([]);
  const [releases, setReleases] = useState<VRelease[]>([]);
  const [releaseChanges, setReleaseChanges] = useState<VReleaseChange[]>([]);
  const [crRatePence, setCrRatePence] = useState(DEFAULT_CR_RATE_PENCE);

  const load = useCallback(async () => {
    if (!loaded.current) setLoading(true);
    setError(null);
    try {
      const { projects: rawProjects } =
        await api.get<{ projects: ApiProject[] }>("/projects");
      const vProjects = rawProjects.map(adaptProject);

      let vClients: VClient[];
      if (isAdmin) {
        const { clients: rawClients } =
          await api.get<{ clients: ApiClient[] }>("/clients");
        vClients = rawClients.map((c) => ({
          id: c.id,
          name: c.name,
          contact: c.contact,
          plan: c.plan,
          since: c.since,
          archived: c.archived_at != null,
        }));
      } else {
        const first = rawProjects[0];
        vClients = [
          {
            id: first?.client_id ?? "me",
            name: first?.client_name ?? "Your account",
            contact: "",
            plan: "",
            since: "",
            archived: false,
          },
        ];
      }

      let vTickets: VTicket[];
      if (isAdmin) {
        const { tickets: rawTickets } =
          await api.get<{ tickets: ApiTicket[] }>("/tickets");
        vTickets = rawTickets.map(adaptTicket);
      } else {
        const perProject = await Promise.all(
          rawProjects.map((p) =>
            api
              .get<{ tickets: ApiTicket[] }>(`/projects/${p.id}/tickets`)
              .then((r) => r.tickets)
              .catch(() => [] as ApiTicket[]),
          ),
        );
        vTickets = perProject.flat().map(adaptTicket);
      }

      const [ms, crs, creds, docs, tks, acts, rels] = await Promise.all([
        api
          .get<{ milestones: ApiMilestone[] }>("/milestones")
          .then((r) => r.milestones)
          .catch(() => [] as ApiMilestone[]),
        api
          .get<{ changeRequests: ApiChangeRequest[]; ratePence?: number }>("/change-requests")
          .catch(() => ({ changeRequests: [] as ApiChangeRequest[], ratePence: undefined })),
        api
          .get<{ credentials: ApiCredential[] }>("/credentials")
          .then((r) => r.credentials)
          .catch(() => [] as ApiCredential[]),
        api
          .get<{ documents: ApiDocument[] }>("/documents")
          .then((r) => r.documents)
          .catch(() => [] as ApiDocument[]),
        api
          .get<{ tasks: ApiTask[] }>("/tasks")
          .then((r) => r.tasks)
          .catch(() => [] as ApiTask[]),
        api
          .get<{ activity: VActivity[] }>("/activity")
          .then((r) => r.activity)
          .catch(() => [] as VActivity[]),
        api
          .get<{ releases: ApiRelease[]; changes: ApiReleaseChange[] }>("/releases")
          .catch(() => ({ releases: [] as ApiRelease[], changes: [] as ApiReleaseChange[] })),
      ]);

      setClients(vClients);
      setProjects(vProjects);
      setTickets(vTickets);
      setMilestones(ms.map(adaptMilestone));
      const rate = crs.ratePence ?? DEFAULT_CR_RATE_PENCE;
      setCrRatePence(rate);
      setChangeRequests(crs.changeRequests.map((cr) => adaptChangeRequest(cr, rate)));
      setCredentials(creds.map(adaptCredential));
      setDocuments(docs.map(adaptDocument));
      setTasks(tks.map(adaptTask));
      setActivity(acts);
      setReleases(rels.releases.map(adaptRelease));
      setReleaseChanges(rels.changes.map(adaptChange));
      loaded.current = true;
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    void load();
  }, [load]);

  return {
    loading,
    error,
    clients,
    projects,
    tickets,
    milestones,
    changeRequests,
    credentials,
    documents,
    tasks,
    activity,
    releases,
    releaseChanges,
    crRatePence,
    reload: () => void load(),
  };
}
